import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <Card className="w-full max-w-[400px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60">
          <CardContent className="p-8 text-center">
            <KeyRound className="h-10 w-10 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Invalid Reset Link</h2>
            <p className="text-sm text-slate-400 mb-6">This password reset link is invalid or missing a token.</p>
            <Link to="/login">
              <Button className="w-full bg-blue-600 hover:bg-blue-500" data-testid="back-to-login-btn">Back to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <Card className="w-full max-w-[400px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60">
          <CardContent className="p-8 text-center">
            <div className="p-3 rounded-full bg-emerald-500/10 border border-emerald-500/20 inline-block mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">Password Reset Successful</h2>
            <p className="text-sm text-slate-400 mb-6">You can now log in with your new password.</p>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-500"
              onClick={() => navigate("/login")}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
      <Card className="w-full max-w-[400px] bg-slate-900/80 backdrop-blur-xl border-slate-800/60 shadow-2xl shadow-black/20">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <div className="p-3 rounded-full bg-blue-500/10 border border-blue-500/20 inline-block mb-4">
              <KeyRound className="h-8 w-8 text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Set New Password
            </h1>
            <p className="text-xs text-slate-500 mt-2">Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs text-slate-400 font-medium uppercase tracking-wider">New Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-new-password"
                  className="h-11 pr-10 bg-slate-950/50 border-slate-800 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 text-slate-200 placeholder:text-slate-600"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-11 w-11 text-slate-500 hover:text-slate-300 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400 font-medium uppercase tracking-wider">Confirm Password</Label>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="reset-confirm-password"
                className="h-11 bg-slate-950/50 border-slate-800 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 text-slate-200 placeholder:text-slate-600"
              />
            </div>

            <div className="text-[11px] text-slate-500 space-y-1">
              <p>Password must contain:</p>
              <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                <li className={newPassword.length >= 8 ? "text-emerald-400" : ""}>At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? "text-emerald-400" : ""}>One uppercase letter</li>
                <li className={/[a-z]/.test(newPassword) ? "text-emerald-400" : ""}>One lowercase letter</li>
                <li className={/[0-9]/.test(newPassword) ? "text-emerald-400" : ""}>One number</li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? "text-emerald-400" : ""}>One special character</li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]"
              disabled={loading || !newPassword || !confirmPassword}
              data-testid="reset-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-slate-500 hover:text-slate-300 inline-flex items-center gap-1" data-testid="back-to-login-link">
              <ArrowLeft className="h-3 w-3" /> Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
